/**
 * The IndexedDB behind the inventory, its photos, and the vision cache.
 *
 * ## Why move at all
 *
 * localStorage is one ~5MB string bucket shared by everything, and photos are
 * the only large thing in there. A hundred items with thumbnails is most of the
 * budget, and the failure is a thrown quota error at the worst possible moment —
 * the save. IndexedDB has no such ceiling worth worrying about, stores
 * structured values rather than one giant JSON string, and doesn't block the
 * main thread parsing it.
 *
 * ## Why photos are their own store
 *
 * Every write to the inventory used to re-serialise every photo in it. Adding
 * one item to a list of eighty rewrote a megabyte of base64 to change one row.
 * Splitting them means a write touches the row it changed and nothing else.
 *
 * The *shape* the app sees is unchanged: `FoodItem.photo` is still a data URL
 * on the item. The split and the rejoin happen entirely inside this layer, so
 * not one component knows about it.
 *
 * ## What stays in localStorage
 *
 * Settings, and the shopping list. Both are small, and settings in particular
 * must be readable **synchronously before first paint** — a theme fetched
 * asynchronously is a white flash on every load.
 */

import { openDB } from "idb";
import type { DBSchema, IDBPDatabase } from "idb";

import type { FoodItem } from "../../features/food/types";
import type { Recognition } from "../../features/vision/types";

export const DB_NAME = "pantrymind";
export const DB_VERSION = 1;

/** An item as stored: everything except the photo, which lives next door. */
export type StoredItem = Omit<FoodItem, "photo">;

interface PantryDB extends DBSchema {
  items: { key: string; value: StoredItem };
  /** Keyed by the item's id, so a photo can never outlive its item by accident. */
  photos: { key: string; value: { id: string; photo: string } };
  /** Keyed by the photo's hash, exactly as the localStorage cache was. */
  vision: { key: string; value: { hash: string; results: Recognition[]; at: number } };
}

let open: Promise<IDBPDatabase<PantryDB>> | null = null;

/**
 * The database, opened once.
 *
 * Rejects rather than throwing synchronously, and every caller in this codebase
 * treats a rejection as "no stored data" — IndexedDB is unavailable in some
 * private-browsing modes, and losing the app to that would be a far worse
 * outcome than losing persistence.
 */
export function db(): Promise<IDBPDatabase<PantryDB>> {
  if (!open) {
    open = openDB<PantryDB>(DB_NAME, DB_VERSION, {
      upgrade(database) {
        if (!database.objectStoreNames.contains("items")) {
          database.createObjectStore("items", { keyPath: "id" });
        }
        if (!database.objectStoreNames.contains("photos")) {
          database.createObjectStore("photos", { keyPath: "id" });
        }
        if (!database.objectStoreNames.contains("vision")) {
          database.createObjectStore("vision", { keyPath: "hash" });
        }
      },
    });
  }
  return open;
}

/**
 * Closes the handle and forgets it. Tests only.
 *
 * The close is the load-bearing half: `deleteDatabase` blocks indefinitely
 * while any connection is open, so a setup that deleted without closing hung
 * the whole suite rather than failing it.
 */
export async function resetDbForTests(): Promise<void> {
  if (!open) return;
  try {
    (await open).close();
  } catch {
    /* already gone */
  }
  open = null;
}

/** True when this browser has IndexedDB at all. */
export function hasIndexedDb(): boolean {
  return typeof indexedDB !== "undefined";
}
